// © Microsoft Corporation. All rights reserved.
/**
 * Default query param prefix used if none specified
 */
export const DefaultQueryParamPrefix = "rd";
/**
 * Generates a plain JavaScript object with the client values.
 * @param values The values to convert.
 */
function objectFromSpecification(values) {
    const requestDataValue = values;
    if (requestDataValue.clientValue !== undefined) {
        // If it's a IRequestDataValueSpecification, return the actual getter
        return requestDataValue.clientValue;
    }
    // Otherwise recurse for each key until we find a plain value type
    return Object
        .keys(values)
        .reduce((pv, cv) => (Object.assign(Object.assign({}, pv), { [cv]: objectFromSpecification(values[cv]) })), {});
}
/**
 * Allows you to pass data about a request between client and server.
 * This interface can be used isomorphically.
 *
 * The "spec" contains "getters" to receive the data when called client side.
 * To operate server-side you must first serialize the client-side data and pass it via
 * query string parameters to the server. You then "load" the query string parameters using
 * "loadQueryParams". Once loaded you can request the data server-side with the same
 * interface as the client.
 *
 * NOTE: The use of URLSearchParams makes this not IE11 compatible without polyfill.
 *
 * @example
 *      interface IRequestContext extends IRequestDataObject {
 *          window: {
 *              innerWidth: IRequestDataValue<number>
 *          }
 *      }
 *
 *      // On client and server
 *      const rd = new RequestData<IRequestContext>(
 *          {
 *              window: {
 *                  innerWidth: {
 *                      clientValue: window.innerWidth
 *                  }
 *              }
 *          });
 *
 *      // Client-side
 *      const innerWidth = rd.data.window.innerWidth();
 *
 *      const serialized = rd.getSerializedData();
 *      // Make-call to server
 *      fetch(`https://assets.msn.com/render?${serialized}`);
 *
 *      // Server-side (in default module)
 *      requestData.loadQueryParams(req.query)
 *      const innerWidth = rd.data.window.innerWidth();
 */
export class RequestData {
    /**
     * @param spec Spec contains serialization metadata as well as client and server value getters
     * @param qspPrefix Prefix to append to serialized query string parameters
     */
    constructor(spec, qspPrefix) {
        this.spec = spec;
        this.qspPrefix = qspPrefix || DefaultQueryParamPrefix;
    }
    get verParam() {
        return `${this.qspPrefix}.ver`;
    }
    /**
     * Will return memoized values in the same structure as IRequestDataSpecification,
     * or the data loaded into
     */
    get data() {
        if (this.memo === undefined) {
            // Assume we're client-side if we haven't memoized query string params
            this.memo = objectFromSpecification(this.spec.spec);
        }
        return this.memo;
    }
    /**
     * Loads all query string parameters prefixed with qspPrefix into request data.
     * @param qsp The query string params to parse. It is safe to pass in the entire query--not just data prefixed by qspPrefix.
     */
    loadQueryParams(qsp) {
        if (this.memo !== undefined) {
            throw "overwriting existing query string parameters.";
        }
        const ver = qsp.get([this.qspPrefix, "ver"].join("."));
        if (ver !== this.spec.ver) {
            throw `unmatched spec version: expected "${this.spec.ver}" but got ${ver}.`;
        }
        this.memo = {};
        // Load each query string parameter one at a time
        qsp.forEach((value, qp) => {
            if (!qp.startsWith(this.qspPrefix)) {
                // We don't belong in this set (we don't have the right prefix)--ignore.
                return;
            }
            if (qp === this.verParam) {
                // Skip version
                return;
            }
            // Split key by dots and remove prefix
            const split = qp.split(".").slice(1);
            let current = this.memo;
            split.forEach((key, i) => {
                let newValue = {}; // assume object unless last in list
                if (i === split.length - 1) {
                    try {
                        newValue = () => JSON.parse(value);
                    }
                    catch (ex) {
                        // Reset memo to undefined and re-throw
                        this.memo = undefined;
                        throw ex;
                    }
                }
                if (!Object.prototype.hasOwnProperty.call(current, key)) {
                    current[key] = newValue;
                }
                if (typeof newValue === "object") {
                    // Already exists--don't overwrite
                    current = current[key];
                    return;
                }
            });
        });
    }
    /**
     * Gets the stored values serialized as strings
     */
    getURLSearchParams() {
        const params = new URLSearchParams();
        /**
         * recurse traverses the data object to build dotted prefixes.
         * e.g.
         *  { a: { b: { c: 5 } } } becomes a.b.c=5
         */
        const recurse = (obj, prefix) => {
            Object.keys(obj).forEach(k => {
                const current = obj[k];
                const currentPrefix = [...prefix, k];
                if (typeof current === "object") {
                    recurse(current, currentPrefix);
                    return;
                }
                // This will put explicit quotes (%22) around strings in the query, which is intentional
                // so JSON.parse is able to determine the correct type for the value.
                params.append(currentPrefix.join("."), JSON.stringify(current()));
            });
        };
        recurse(this.data, [this.qspPrefix]);
        params.sort();
        // Version is always at the end
        params.append(this.verParam, this.spec.ver);
        return params;
    }
    /**
     * Sort the search params and serialize them to string.
     */
    getSerializedData() {
        return this.getURLSearchParams().toString();
    }
}
